import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Notification from '../Notification/Notification'; // Import the Notification component
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: '',
  });
  const [notification, setNotification] = useState({ message: '', type: '' }); // State for notification
  const [loading, setLoading] = useState(false); // Add loading state

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); // Disable the button and show the spinner
    setNotification({ message: '', type: '' }); // Clear previous notifications

    try {
      const response = await fetch('https://sriharim.in/php/submit-contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorText = await response.text(); // Get raw response for debugging
        throw new Error(`HTTP error! Status: ${response.status}, Response: ${errorText}`);
      }

      let result;
      try {
        result = await response.json();
      } catch (jsonError) {
        const rawResponse = await response.text(); // Get raw response if JSON parsing fails
        throw new Error(`Failed to parse JSON: ${jsonError.message}, Raw Response: ${rawResponse}`);
      }

      if (result.status === 'success') {
        // Success: Show success notification with contact_id and reset form
        setNotification({ 
          message: `${result.message} (Contact ID: ${result.contact_id})`, 
          type: 'success' 
        });
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          message: '',
        });
        setLoading(false); // Re-enable the button on success
        // Clear notification after 5 seconds
        setTimeout(() => setNotification({ message: '', type: '' }), 5000);
      } else {
        // Error: Show error notification
        setNotification({ message: result.message || 'Failed to submit form.', type: 'error' });
        setLoading(false); // Re-enable the button on error
      }
    } catch (error) {
      console.error('Form submission error:', error); // Log the error for debugging
      setNotification({ message: `An error occurred: ${error.message}`, type: 'error' });
      setLoading(false); // Re-enable the button on error
    }
  };

  const closeNotification = () => {
    setNotification({ message: '', type: '' });
  };

  return (
    <>
      <Helmet>
        <title>Contact Srihari Muppalla | Full Stack Developer & Web Developer & Email Developer in Bangalore</title>
        <meta
          name="description"
          content="Get in touch with Srihari Muppalla, a Full Stack Developer in Bangalore, India. Contact me for web development, email development, or full-stack project inquiries."
        />
        <meta
          name="keywords"
          content="Srihari Muppalla, Full Stack Developer, contact, Bangalore, India, web development, email development"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Srihari Muppalla" />
        <link rel="canonical" href="https://sriharim.in/contact" />

        {/* Open Graph Tags */}
        <meta
          property="og:title"
          content="Contact Srihari Muppalla | Full Stack Developer & Web Developer & Email Developer in Bangalore"
        />
        <meta
          property="og:description"
          content="Get in touch with Srihari Muppalla, a Full Stack Developer in Bangalore, India. Contact me for web development, email development, or full-stack project inquiries."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sriharim.in/contact" />
        <meta property="og:image" content="https://sriharim.in/Logo.png" />
        <meta property="og:image:alt" content="Contact Srihari Muppalla" />
        <meta property="og:site_name" content="Srihari Muppalla Portfolio" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Contact Srihari Muppalla | Full Stack Developer & Web Developer & Email Developer in Bangalore"
        />
        <meta
          name="twitter:description"
          content="Get in touch with Srihari Muppalla, a Full Stack Developer in Bangalore, India. Contact me for web development, email development, or full-stack project inquiries."
        />
        <meta name="twitter:image" content="https://sriharim.in/Logo.png" />
        <meta name="twitter:image:alt" content="Contact Srihari Muppalla" />
        <meta name="twitter:creator" content="@srihariM64" />
      </Helmet>
      <div className="contact-container">
        <h1 className="contact-title">Contact Me</h1>
        <p className="contact-subtitle">
          Get in touch with me for inquiries, collaborations, or to discuss your next project. I'm here to help!
        </p>
        <div className="contact-grid">
          {/* Left Side: Contact Form */}
          <div className="contact-form-section">
            <h2 className="contact-section-title">Send a Message</h2>
            {/* Notification Display */}
            <Notification
              message={notification.message}
              type={notification.type}
              onClose={closeNotification}
            />
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="contact-form-group">
                <label htmlFor="fullName">Full Name</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                  data-gramm="false" // Prevent Grammarly interference
                />
              </div>
              <div className="contact-form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  required
                  data-gramm="false" // Prevent Grammarly interference
                />
              </div>
              <div className="contact-form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                  required
                  data-gramm="false" // Prevent Grammarly interference
                />
              </div>
              <div className="contact-form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Enter your message"
                  rows="5"
                  required
                  data-gramm="false" // Prevent Grammarly interference
                ></textarea>
              </div>
              <button
                type="submit"
                className="contact-submit-btn"
                disabled={loading} // Disable the button when loading is true
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                  </>
                ) : (
                  'Submit'
                )}
              </button>
            </form>
          </div>

          {/* Right Side: Contact Information */}
          <div className="contact-info-section">
            <h2 className="contact-section-title">Contact Information</h2>
            <div className="contact-info">
              <p><strong>Name:</strong> Srihari Muppalla</p>
              <p><strong>Email:</strong> srihari@sriharim.in</p>
              <p><strong>Phone:</strong> +91 9346359910</p>
              <p><strong>Address:</strong> Vidyaranyapura, Bengaluru, Karnataka, India.</p>
            </div>
            <div className="contact-socials">
              <h3>Follow Me</h3>
              <div className="social-icons">
                <a
                  href="https://www.linkedin.com/in/srihari-muppalla/"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="LinkedIn"
                  aria-label="Follow Srihari Muppalla on LinkedIn"
                >
                  <i className="fab fa-linkedin-in"></i>
                </a>
                <a
                  href="https://github.com/srihariMuppalla"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="GitHub"
                  aria-label="Follow Srihari Muppalla on GitHub"
                >
                  <i className="fab fa-github"></i>
                </a>
                <a
                  href="https://x.com/srihariM64"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Twitter"
                  aria-label="Follow Srihari Muppalla on Twitter"
                >
                  <i className="fa-brands fa-x-twitter"></i>
                </a>
                <a
                  href="https://www.instagram.com/srihari_developer/"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Instagram"
                  aria-label="Follow Srihari Muppalla on Instagram"
                >
                  <i className="fab fa-instagram"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;